import { Pipe, PipeTransform } from '@angular/core';
import { CountryAlpha2Code, roundMonetaryValue, UsStateAbbreviation } from '@aiwyn/shared';
import { BillingDetails, CardFunding, PaymentMethodSubType, PaymentMethodType } from '@aiwyn/api';
import { Store } from '@ngxs/store';
import { AbstractPaymentMethod, GetTotalOptions, PaymentsState } from '@aiwyn/common/payments';
import { CommonSettingsState } from '@aiwyn/common/settings';

@Pipe({
  name: 'addPaymentFees',
  standalone: false,
})
export class AddPaymentFeesPipe implements PipeTransform {

  constructor(private readonly store: Store) {}

  transform(
    subtotal: number | string,
    paymentMethodType: string,
    isPaymentPlan: boolean,
    usState: UsStateAbbreviation,
    country: CountryAlpha2Code,
    clientId: number,
    businessEntityId: number,
  ): number {
    subtotal = +subtotal;

    paymentMethodType = paymentMethodType.toLowerCase();
    const pseudoPaymentMethod = AddPaymentFeesPipe._getPseudoPaymentMethod(
      [CardFunding.CREDIT, CardFunding.DEBIT].includes(paymentMethodType as CardFunding) ? PaymentMethodType.CARD : paymentMethodType,
      paymentMethodType,
      country,
      usState,
    );
    const getTotalOptions: GetTotalOptions = {
      subtotal,
      isPaymentPlan,
      ignoreLateFees: true,
      currencyCode: this.store.selectSnapshot(CommonSettingsState.getClientCurrency(clientId))?.code,
      businessEntityId,
    };

    const processingFee = this.store.selectSnapshot(PaymentsState.getProcessingFee(pseudoPaymentMethod, getTotalOptions));
    const internationalFee = this.store.selectSnapshot(PaymentsState.getInternationalFee(pseudoPaymentMethod, getTotalOptions));

    return roundMonetaryValue(subtotal + processingFee + internationalFee);
  }

  // please ignore this atrocity
  private static _getPseudoPaymentMethod(
    paymentMethodType: string,
    paymentMethodSubType: string,
    country: CountryAlpha2Code,
    usState: UsStateAbbreviation,
  ): AbstractPaymentMethod {
    return {
      get type(): PaymentMethodType {
        return paymentMethodType as PaymentMethodType;
      },

      get subType(): PaymentMethodSubType {
        return paymentMethodSubType as PaymentMethodSubType;
      },

      get isCreditCard(): boolean {
        return this.subType === CardFunding.CREDIT;
      },

      get country(): CountryAlpha2Code {
        if (country) {
          return country;
        }

        switch (this.type) {
          case PaymentMethodType.ACSS:
          case PaymentMethodType.ACSS_DEBIT:
            return CountryAlpha2Code.CANADA;
          case PaymentMethodType.ACH:
          case PaymentMethodType.US_BANK_ACCOUNT:
            return CountryAlpha2Code.UNITED_STATES;
          default:
            return undefined;
        }
      },

      get billingDetails(): BillingDetails {
        return {
          address: {
            state: usState,
          } as BillingDetails['address'],
        } as BillingDetails;
      },
    } as AbstractPaymentMethod;
  }
}
